/**
 * Selection health tab (13 weeks): ABC classes against the manual tier, the long tail, launches against the
 * category median, return-rate outliers, inactive SKUs with and without stock.
 * Props: data (GET /api/review/selection).
 */
import React from 'react';
import { DataTable, Empty, Metric, Money, Notes, Num, Pct, Section, SkuCell } from './ReviewPieces';

const ABC_TONE = { A: 'bg-green-600', B: 'bg-blue-500', C: 'bg-gray-400' };

export const SelectionTab = ({ data }) => {
  if (!data) return null;
  const abc = data.abc || {};
  const inactive = data.inactive || {};
  return (
    <div className="space-y-4" data-testid="review-selection-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {['A', 'B', 'C'].map(grade => (
          <Metric key={grade} label={`Class ${grade}`} entry={{ value: abc[grade]?.skus }} kind="number"
            footnote={abc[grade] ? `${abc[grade].sku_share_pct}% of SKUs · ${abc[grade].revenue_share_pct}% of revenue` : undefined} testId={`review-abc-${grade}`} />
        ))}
        <Metric label="Long tail" entry={{ value: data.long_tail?.skus }} kind="number" footnote={`${data.long_tail?.share_of_skus_pct ?? '—'}% of SKUs make the bottom 5% of revenue`} />
      </div>
      {Object.keys(abc).length > 0 && (
        <div className="flex h-3 overflow-hidden rounded-full bg-gray-100" role="img" aria-label={['A', 'B', 'C'].map(g => `${g}: ${abc[g]?.revenue_share_pct ?? 0}% of revenue`).join(', ')}>
          {['A', 'B', 'C'].map(g => <div key={g} className={ABC_TONE[g]} style={{ width: `${abc[g]?.revenue_share_pct || 0}%` }} title={`${g}: ${abc[g]?.revenue_share_pct ?? 0}% of revenue from ${abc[g]?.skus ?? 0} SKUs`} />)}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Tier mismatches" subtitle="The manual tier on the SKU master differs from the ABC class the sales give." testId="review-tier-mismatches">
          <DataTable rows={data.tier_mismatches} empty="Every tier matches its ABC class." columns={[
            { key: 'sku', label: 'SKU', render: row => <span className="font-mono text-xs">{row.sku_code}</span> },
            { key: 'manual', label: 'Tier set' },
            { key: 'computed', label: 'Sales say' },
            { key: 'revenue', label: '13-wk revenue', align: 'right', render: row => <Money v={row.revenue} /> },
          ]} />
        </Section>
        <Section title="Top A SKUs" subtitle="The SKUs making the first 80% of revenue.">
          <DataTable rows={data.a_skus?.slice(0, 15)} empty="No sales in the last 13 weeks." columns={[
            { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
            { key: 'revenue', label: '13-wk revenue', align: 'right', render: row => <Money v={row.revenue} /> },
          ]} />
        </Section>
      </div>

      <Section title="Launches" subtitle="First sale within 90 days: the first 4 weeks against the category's median weekly units." testId="review-launches">
        <DataTable rows={data.launches} empty="No launches in the last 90 days." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'category', label: 'Category' },
          { key: 'first_sale', label: 'First sale' },
          { key: 'weeks_since_launch', label: 'Weeks', align: 'right', render: row => <Num v={row.weeks_since_launch} /> },
          { key: 'avg_weekly_units', label: 'Units/week', align: 'right', render: row => <span className="tabular-nums">{row.avg_weekly_units}</span> },
          { key: 'category_median_weekly_units', label: 'Category median', align: 'right', render: row => <span className="tabular-nums">{row.category_median_weekly_units ?? '—'}</span> },
          { key: 'ramp_pct', label: 'Ramp', align: 'right', render: row => <span className={`font-semibold tabular-nums ${row.ramp_pct == null ? 'text-gray-400' : row.ramp_pct < 50 ? 'text-red-700' : row.ramp_pct >= 100 ? 'text-green-700' : 'text-amber-700'}`}>{row.ramp_pct == null ? '—' : `${row.ramp_pct}%`}</span> },
        ]} />
      </Section>

      <Section title="Return-rate outliers" subtitle="Well above the category's return rate, with enough volume to mean something." testId="review-return-outliers">
        <DataTable rows={data.return_outliers} empty="No SKU returns far more than its category." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'category', label: 'Category' },
          { key: 'units', label: 'Units', align: 'right', render: row => <Num v={row.units} /> },
          { key: 'returns', label: 'Returns', align: 'right', render: row => <Num v={row.returns} /> },
          { key: 'return_rate_pct', label: 'Rate', align: 'right', render: row => <span className="font-semibold text-red-700"><Pct v={row.return_rate_pct} /></span> },
          { key: 'category_rate_pct', label: 'Category', align: 'right', render: row => <Pct v={row.category_rate_pct} /> },
        ]} />
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Inactive with stock" subtitle={`No sale in 60 days · ${inactive.with_stock_count ?? 0} SKUs`} right={<span className="text-sm font-semibold tabular-nums"><Money v={inactive.with_stock_value_mrp} /></span>}>
          <DataTable rows={inactive.with_stock} empty="Nothing sits unsold with stock." columns={[
            { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
            { key: 'brand', label: 'Brand' },
            { key: 'stock', label: 'Stock', align: 'right', render: row => <Num v={row.stock} /> },
            { key: 'stock_value_mrp', label: 'At list price', align: 'right', render: row => <Money v={row.stock_value_mrp} /> },
          ]} />
        </Section>
        <Section title="Inactive without stock" subtitle={`No sale in 60 days and no stock · ${inactive.without_stock_count ?? 0} SKUs · candidates to delist or relaunch`}>
          {inactive.without_stock?.length ? (
            <ul className="flex flex-wrap gap-1.5 text-xs">
              {inactive.without_stock.slice(0, 60).map(row => <li key={row.sku_code} className="rounded bg-gray-100 px-1.5 py-0.5 font-mono" title={row.product_name}>{row.sku_code}</li>)}
              {inactive.without_stock_count > 60 && <li className="px-1.5 py-0.5 text-gray-500">+{inactive.without_stock_count - 60} more</li>}
            </ul>
          ) : <Empty>Every SKU without stock has sold recently.</Empty>}
        </Section>
      </div>
      <Notes notes={data.notes} />
    </div>
  );
};

export default SelectionTab;
